<template>
  <div>
    <BaseCard
      v-if="state === states.READY"
      data-test="candidate-billing"
    >
      <header class="mb-6 md:flex md:items-center md:justify-between">
        <div class="flex">
          <div class="flex-shrink-0 mr-4">
            <span class="flex flex-col items-center justify-center w-10 h-10 text-white rounded-full bg-secondary">
              <Icon
                width="24px"
                height="24px"
                view-box="0 0 24 24"
              >
                <Receipt />
              </Icon>
            </span>
          </div>
          <div>
            <h2 class="text-lg">
              Pricing
            </h2>
            <p
              class="mt-1 text-gray-700"
              data-test="offer-sentence"
            >
              {{ sentence }}
            </p>
          </div>
        </div>
        <div
          v-if="!billing.has_card"
          class="flex items-center justify-end mt-4 md:mt-0"
        >
          <label class="text-sm text-gray-600">
            <span class="mr-2">Currency</span>
            <select
              v-model="currency"
              class="form-select"
              data-test="currency"
            >
              <option
                v-for="option in billing.currencies"
                :key="option"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
          </label>
        </div>
      </header>

      <div
        v-if="billing.blocked"
        class="p-4 mb-6 border border-red-300 rounded bg-red-50"
        data-test="blocked-notice"
      >
        <p class="font-medium text-red-800">
          Your last monthly payment did not go through, so new results are locked.
        </p>
        <p class="mt-1 text-sm text-red-800">
          Update your card below and we will retry the payment straight away. Results you have already unlocked stay open.
          <a
            v-if="unpaidInvoiceUrl"
            :href="unpaidInvoiceUrl"
            class="underline"
            target="_blank"
            rel="noopener"
          >Or pay the invoice on Stripe.</a>
        </p>
      </div>

      <div class="grid gap-6 md:grid-cols-2">
        <div class="px-6 py-4 border rounded-lg">
          <p class="text-sm text-gray-600">
            Free results used
          </p>
          <p
            class="mt-1 text-3xl font-bold leading-none"
            data-test="free-used"
          >
            {{ billing.free_used }} <span class="text-lg font-normal text-gray-600">of {{ billing.free_candidates }}</span>
          </p>
          <p
            class="mt-3 text-sm text-gray-600"
            data-test="free-explainer"
          >
            <span v-if="billing.free_remaining > 0">
              Your next {{ billing.free_remaining }} {{ billing.free_remaining === 1 ? 'candidate’s results are' : 'candidates’ results are' }} free. Add a card whenever you like; nothing is charged until you unlock a sixth candidate.
            </span>
            <span v-else-if="!billing.has_card">
              Your free results are used. Add a card to unlock further results; you are charged {{ unitAmount }} per candidate, once a month for the candidates you unlocked.
            </span>
            <span v-else>
              You are charged {{ unitAmount }} per candidate whose results you unlock, once a month.
            </span>
          </p>
        </div>

        <div
          v-if="billing.has_card"
          class="px-6 py-4 border rounded-lg"
          data-test="card-on-file"
        >
          <p class="text-sm text-gray-600">
            Card on file
          </p>
          <p class="mt-1 text-lg font-medium">
            <span v-if="billing.card">{{ cardBrand }} ending {{ billing.card.last4 }}<span class="text-sm font-normal text-gray-600"> (expires {{ billing.card.exp_month }}/{{ billing.card.exp_year }})</span></span>
            <span v-else>A card is saved with Stripe</span>
          </p>
          <p class="mt-3 text-sm text-gray-600">
            Billed monthly in {{ billing.currency }}<span v-if="nextInvoice">; next invoice on {{ formatDate(nextInvoice) }}</span>.
          </p>
          <div class="flex flex-wrap gap-2 mt-4">
            <BaseButton
              size="small"
              @click="showCardForm = !showCardForm"
            >
              {{ billing.blocked ? 'Update card' : 'Change card' }}
            </BaseButton>
            <BaseButton
              variant="salmon"
              size="small"
              @click="showRemoveModal = true"
            >
              Remove card and stop billing
            </BaseButton>
          </div>
        </div>
      </div>

      <form
        v-if="showCardForm || !billing.has_card"
        ref="form"
        class="max-w-lg mt-8"
        novalidate
        data-test="card-form"
        @submit.prevent
      >
        <p class="mb-3 font-medium">
          {{ billing.has_card ? 'New card' : 'Add a card' }}
        </p>
        <div
          v-if="cardFormState === 'loading'"
          class="my-6"
        >
          <Loader />
          <p class="mt-2 text-sm text-center text-gray-600">
            Loading the card form
          </p>
        </div>
        <p
          v-else-if="cardFormState === 'unavailable'"
          class="p-4 text-sm border rounded bg-gray-50"
          data-test="card-form-unavailable"
        >
          The card form could not load. Check your connection and try again; nothing has been charged.
          <button
            type="button"
            class="ml-1 underline"
            @click="startCardForm()"
          >
            Try again
          </button>
        </p>
        <div
          v-show="cardFormState === 'ready'"
          id="candidate-card-element"
          class="mb-4"
        >
          <!-- Stripe Payment Element (setup mode) mounts here -->
        </div>
        <p
          v-if="cardFormState === 'ready'"
          class="mb-4 text-sm text-gray-600"
          data-test="card-form-terms"
        >
          Nothing is charged now. {{ unitAmount }} per candidate whose results you unlock, invoiced monthly to this card. Cancel any time from this page.
        </p>
        <BaseButton
          v-if="cardFormState === 'ready'"
          class="justify-center w-full"
          :loading="saving"
          @click="submitCard()"
        >
          Save card
        </BaseButton>
        <p
          v-if="errorMessage"
          class="mt-3 text-sm text-red-700"
          data-test="card-error"
        >
          {{ errorMessage }}
        </p>
      </form>

      <Modal
        :open="showRemoveModal"
        :can-close="!removing"
        @close="showRemoveModal = false"
      >
        <div class="p-6">
          <h3 class="text-lg font-medium">
            Remove card and stop billing
          </h3>
          <p class="my-3">
            Candidates you have unlocked this month are invoiced now. After that nothing is charged, and results beyond your free {{ billing.free_candidates }} stay locked until you add a card again.
          </p>
          <p class="inline-flex space-x-2">
            <BaseButton
              :loading="removing"
              @click="removeCard()"
            >
              Yes, remove card
            </BaseButton>
            <BaseButton
              variant="salmon"
              @click="showRemoveModal = false"
            >
              Keep card
            </BaseButton>
          </p>
          <p
            v-if="removeError"
            class="mt-3 text-sm text-red-700"
          >
            {{ removeError }}
          </p>
        </div>
      </Modal>
    </BaseCard>
    <BaseCard
      v-else-if="state === states.LOADING"
      class="my-8"
    >
      <Loader />
    </BaseCard>
    <BaseCard v-else-if="state === states.ERROR">
      <BaseErrorBlock />
    </BaseCard>
  </div>
</template>

<script>
import Icon from '@components/Icons/Icon'
import Loader from '@components/Loader'
import Modal from '@components/Modal'
import Receipt from '@components/Icons/Receipt'

import candidateBillingApi from '@api/candidateBilling'
import states from '@api/states'
import { formatDate } from '@utils/formatDate'
import { mapGetters } from 'vuex'
import { offerSentence, unitAmountFor, formatMinor, nextInvoiceDate, chooseCurrency } from '@utils/candidateBilling'

/**
 * The plan page for organisations billed per candidate: one sentence, the
 * free allowance, the card on file (or the card form) and the block notice
 * after a failed monthly charge. Card setup uses Stripe's Payment Element in
 * setup mode against a SetupIntent; no charge happens here.
 */
export default {
  components: {
    Icon,
    Loader,
    Modal,
    Receipt
  },

  data() {
    return {
      states,
      formatDate,
      error: null,
      currency: null,
      suggestion: null,
      showCardForm: false,
      cardFormState: 'idle',
      stripe: null,
      elements: null,
      paymentElement: null,
      setupIntentId: null,
      saving: false,
      errorMessage: null,
      showRemoveModal: false,
      removing: false,
      removeError: null
    }
  },

  computed: {
    ...mapGetters({
      billing: 'subscriptions/candidateBilling',
      hasBillingLoaded: 'subscriptions/hasBillingLoaded'
    }),

    state() {
      if (this.error) {
        return states.ERROR
      }
      if (!this.hasBillingLoaded || !this.billing || !this.currency) {
        return states.LOADING
      }
      return states.READY
    },

    sentence() {
      return offerSentence(this.billing, this.currency)
    },

    unitAmount() {
      const amount = unitAmountFor(this.billing, this.currency)
      return amount === null ? '' : formatMinor(amount, this.currency)
    },

    cardBrand() {
      const brand = this.billing && this.billing.card ? String(this.billing.card.brand || 'Card') : 'Card'
      return brand.charAt(0).toUpperCase() + brand.slice(1)
    },

    nextInvoice() {
      return nextInvoiceDate(this.billing)
    },

    unpaidInvoiceUrl() {
      const subscription = this.billing && this.billing.subscription
      return subscription && subscription.unpaid_invoice ? subscription.unpaid_invoice.hosted_invoice_url : null
    },

    returnUrl() {
      const path = this.$router.resolve({ name: 'settings-card-complete', query: { currency: this.currency } }).href
      return window.location.origin + path
    }
  },

  watch: {
    currency(value, previous) {
      if (previous && value !== previous && this.cardFormState !== 'idle') {
        this.startCardForm()
      }
    },
    state(value) {
      if (value === states.READY && !this.billing.has_card && this.cardFormState === 'idle') {
        this.startCardForm()
      }
    },
    showCardForm(value) {
      if (value && this.cardFormState === 'idle') {
        this.startCardForm()
      }
    }
  },

  mounted() {
    this.load()
  },

  methods: {
    load() {
      Promise.all([
        this.$store.dispatch('subscriptions/getCandidateBilling'),
        candidateBillingApi.pricing().catch(() => null)
      ])
        .then(([billing, pricing]) => {
          this.suggestion = pricing ? pricing.currencySuggestion : null
          this.currency = chooseCurrency(billing, this.suggestion, pricing ? pricing.defaultCurrency : null)
        })
        .catch(error => {
          this.error = error
        })
    },

    /**
     * Create a SetupIntent and mount the Payment Element in setup mode. If
     * Stripe.js is not available the form says so rather than hanging.
     */
    startCardForm() {
      this.cardFormState = 'loading'
      this.errorMessage = null
      candidateBillingApi.createSetupIntent(this.currency)
        .then(data => {
          this.setupIntentId = data.setupIntentId
          if (!window.Stripe || !process.env.VUE_APP_STRIPE_PUBLISHABLE_TC) {
            this.cardFormState = 'unavailable'
            return
          }
          this.stripe = this.stripe || window.Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_TC)
          if (this.paymentElement) {
            this.paymentElement.unmount()
          }
          this.elements = this.stripe.elements({ clientSecret: data.clientSecret, appearance: { theme: 'stripe' } })
          this.paymentElement = this.elements.create('payment')
          this.paymentElement.on('ready', () => { this.cardFormState = 'ready' })
          this.paymentElement.on('loaderror', () => { this.cardFormState = 'unavailable' })
          this.$nextTick(() => this.paymentElement.mount('#candidate-card-element'))
        })
        .catch(() => {
          this.cardFormState = 'unavailable'
        })
    },

    /**
     * Confirm the SetupIntent. Stripe redirects to the card-complete page
     * when the bank needs a challenge; otherwise the intent is confirmed
     * here and completed on the server straight away.
     */
    submitCard() {
      if (this.saving || !this.stripe || !this.elements) {
        return
      }
      this.saving = true
      this.errorMessage = null
      this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: { return_url: this.returnUrl },
        redirect: 'if_required'
      })
        .then(result => {
          if (result.error) {
            this.errorMessage = result.error.message || 'The card could not be saved.'
            return
          }
          const intent = result.setupIntent
          return candidateBillingApi.completeSetup({
            setupIntentId: intent.id,
            setupIntentClientSecret: intent.client_secret,
            currency: this.currency
          })
            .then(() => this.$store.dispatch('subscriptions/getCandidateBilling'))
            .then(() => {
              this.showCardForm = false
              this.cardFormState = 'idle'
              if (this.paymentElement) {
                this.paymentElement.unmount()
                this.paymentElement = null
              }
            })
        })
        .catch(() => {
          this.errorMessage = 'Sorry, we could not save the card right now. Nothing has been charged.'
        })
        .finally(() => {
          this.saving = false
        })
    },

    removeCard() {
      if (this.removing) {
        return
      }
      this.removing = true
      this.removeError = null
      this.$store.dispatch('subscriptions/removeBillingCard')
        .then(() => {
          this.showRemoveModal = false
          this.cardFormState = 'idle'
        })
        .catch(() => {
          this.removeError = 'Sorry, we could not remove the card right now.'
        })
        .finally(() => {
          this.removing = false
        })
    }
  }
}
</script>
